import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { type SxProps, type Theme } from '@mui/material/styles';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

type CalendarHeaderCardProps = {
  stickyTop?: number | Record<string, number>;
  controls?: React.ReactNode;
  navigation?: React.ReactNode;
  workHours?: React.ReactNode;
};

const todayButtonSx: SxProps<Theme> = (theme) => ({
  textTransform: 'none',
  fontWeight: 700,
  borderColor: theme.palette.primary.main,
  color: theme.palette.primary.main,
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
});

export const CalendarHeaderCard: React.FC<CalendarHeaderCardProps> = ({
  stickyTop,
  controls,
  navigation,
  workHours,
}) => {
  const isSticky = stickyTop !== undefined;

  return (
    <Box
      sx={{
        ...(isSticky
          ? {
              position: 'sticky' as const,
              top: stickyTop,
              zIndex: (theme: Theme) => theme.zIndex.appBar - 2,
              mx: { xs: -1, sm: 0 },
              backgroundColor: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(8px)',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: { xs: 1.5, sm: 2 },
              boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
            }
          : {}),
        pt: isSticky ? { xs: 2, sm: 2.25 } : { xs: 0.75, sm: 1 },
        pb: { xs: 0.75, sm: 1 },
        px: { xs: 1, sm: 1.25 },
        mb: 1.25,
      }}
    >
      {controls}
      {controls && (navigation || workHours) && (
        <Divider sx={{ my: { xs: 0.5, sm: 0.75 }, opacity: 0.7 }} />
      )}
      {navigation}
      {navigation && workHours && (
        <Divider sx={{ my: { xs: 0.5, sm: 0.75 }, opacity: 0.7 }} />
      )}
      {workHours}
    </Box>
  );
};

type CalendarNavigationBarProps = {
  title: string;
  currentViewType: string;
  onlyDayView: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onToday: () => void;
  onWeek: () => void;
  onMonth: () => void;
  onTitleClick?: (anchorEl: HTMLElement) => void;
};

export const CalendarNavigationBar: React.FC<CalendarNavigationBarProps> = ({
  title,
  currentViewType,
  onlyDayView,
  onPrevious,
  onNext,
  onToday,
  onWeek,
  onMonth,
  onTitleClick,
}) => {
  const titleNode = (fontSize: string | Record<string, string>) => (
    <Typography
      variant="h6"
      onClick={(event) => onTitleClick?.(event.currentTarget)}
      sx={{
        fontWeight: 700,
        fontSize,
        lineHeight: 1.2,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        wordBreak: 'break-word',
        hyphens: 'auto',
        cursor: onTitleClick ? 'pointer' : 'default',
        px: 1,
      }}
    >
      {title}
    </Typography>
  );

  const viewButtons = !onlyDayView && (
    <>
      <Button
        size="small"
        variant={currentViewType === 'timeGridWeek' ? 'contained' : 'outlined'}
        color="primary"
        onClick={onWeek}
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        Week
      </Button>
      <Button
        size="small"
        variant={currentViewType === 'dayGridMonth' ? 'contained' : 'outlined'}
        color="primary"
        onClick={onMonth}
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        Month
      </Button>
    </>
  );

  return (
    <>
      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          position: 'relative',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            columnGap: { xs: 0.75, sm: 1.5, md: 2, lg: 2.5 },
            minWidth: 0,
          }}
        >
          <IconButton color="primary" size="small" onClick={onPrevious}>
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
          {titleNode({ xs: '1.05rem', sm: '1.3rem', md: '1.45rem', lg: '1.6rem', xl: '1.7rem' })}
          <IconButton color="primary" size="small" onClick={onNext}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', columnGap: 1 }}>
          <Button size="small" variant="outlined" color="primary" onClick={onToday} sx={todayButtonSx}>
            Today
          </Button>
          {viewButtons}
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: 'flex', sm: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', minWidth: 0, columnGap: 1 }}>
          <IconButton color="primary" size="small" onClick={onPrevious}>
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
          {titleNode({ xs: '1.25rem' })}
          <IconButton color="primary" size="small" onClick={onNext}>
            <ArrowForwardIos fontSize="small" />
          </IconButton>
        </Box>
        <Box>
          <Button size="small" variant="outlined" color="primary" onClick={onToday} sx={todayButtonSx}>
            Today
          </Button>
        </Box>
      </Box>
    </>
  );
};

type WorkHoursSummaryBarProps = {
  hoursLabel: string;
  breakLabel?: string | null;
  embeddedInCard?: boolean;
  onClick: () => void;
};

export const WorkHoursSummaryBar: React.FC<WorkHoursSummaryBarProps> = ({
  hoursLabel,
  breakLabel,
  embeddedInCard = false,
  onClick,
}) => (
  <Box
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    }}
    sx={{
      border: embeddedInCard ? 0 : '1px solid',
      borderColor: 'divider',
      borderRadius: 1.5,
      px: 1.25,
      py: 0.85,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: embeddedInCard ? 'transparent' : 'background.paper',
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'action.hover' },
    }}
  >
    <Box>
      <Typography variant="body2">
        <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>Hours:</Box>{' '}
        <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>{hoursLabel}</Box>
      </Typography>
      {breakLabel && (
        <Typography variant="body2" sx={{ mt: 0.25 }}>
          <Box component="span" sx={{ fontWeight: 800, color: 'text.primary' }}>Break:</Box>{' '}
          <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>{breakLabel}</Box>
        </Typography>
      )}
    </Box>
    <EditOutlinedIcon fontSize="small" color="action" />
  </Box>
);
